import React, { useEffect, useRef, useState } from "react";
import { fetchAuthenticatedRaw } from "../services/tokenHelper";
import { mediaFetchLimiter } from "../services/mediaFetchLimiter";
import { albumDebug } from "../utils/albumDebug";

const thumbStats = {
  mounted: 0,
  fetchStarted: 0,
  fetchDone: 0,
  fetchFailed: 0,
};

interface AuthenticatedImageProps {
  src: string; // Full URL to the API endpoint
  alt: string;
  className?: string;
  placeholder?: string;
  onLoad?: () => void;
  onError?: () => void;
  loading?: "lazy" | "eager";
}

/**
 * Image component that fetches images from authenticated API endpoints.
 * Converts the response to a blob URL and displays it in an img tag.
 * Uses the existing tokenHelper for authentication.
 */
const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({
  src,
  alt,
  className = "",
  placeholder = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik04NS4zMzMzIDc1TDEyNS4zMzMgMTE1TDE0MS4zMzMgOTlMMTY2LjY2NyAxMjVIMzMuMzMzM0w1OC42NjY3IDk5TDg1LjMzMzMgNzVaIiBmaWxsPSIjQ0REQkU0Ii8+CjxwYXRoIGQ9Ik04NS4zMzMzIDc1TDEyNS4zMzMgMTE1TDE0MS4zMzMgOTlMMTY2LjY2NyAxMjVIMzMuMzMzM0w1OC42NjY3IDk5TDg1LjMzMzMgNzVaIiBmaWxsPSIjQzlEQkUxIi8+CjxjaXJjbGUgY3g9Ijc1IiBjeT0iNzUiIHI9IjgiIGZpbGw9IiNDOURCRTEiLz4KPC9zdmc+",
  onLoad,
  onError,
  loading = "lazy",
}) => {
  const [imageSrc, setImageSrc] = useState<string>(placeholder);
  const [isLoading, setIsLoading] = useState(loading === "eager");
  const [hasError, setHasError] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(loading === "eager");
  const blobUrlRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);
  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    thumbStats.mounted += 1;
    albumDebug("thumb.mount", {
      src,
      loading,
      mounted: thumbStats.mounted,
      limiter: mediaFetchLimiter.getStats(),
    });
    return () => {
      thumbStats.mounted = Math.max(0, thumbStats.mounted - 1);
    };
  }, [loading, src]);

  useEffect(() => {
    if (loading === "eager") {
      setShouldFetch(true);
      return;
    }

    setShouldFetch(false);
    const node = imgRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setShouldFetch(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setShouldFetch(true);
          observer.disconnect();
        }
      },
      { rootMargin: "250px", threshold: 0.01 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loading, src]);

  useEffect(() => {
    if (!shouldFetch) {
      return;
    }

    const requestId = ++requestIdRef.current;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 25_000);

    const fetchImage = async () => {
      let release: (() => void) | null = null;
      const fetchStartedAt = performance.now();
      try {
        setIsLoading(true);
        setHasError(false);
        // Avoid showing the previous image while the new one is loading.
        setImageSrc(placeholder);

        // Clean up previous blob URL if it exists (prevents stale image display on slow loads).
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = null;
        }

        // Avoid stampeding the backend/object storage with hundreds of parallel blob downloads.
        const waitStartedAt = performance.now();
        release = await mediaFetchLimiter.acquire({ signal: controller.signal });
        const limiterWaitMs = Number((performance.now() - waitStartedAt).toFixed(1));
        thumbStats.fetchStarted += 1;
        albumDebug("thumb.fetch start", {
          src,
          limiterWaitMs,
          limiter: mediaFetchLimiter.getStats(),
          fetchStarted: thumbStats.fetchStarted,
        });

        const response = await fetchAuthenticatedRaw(src, {
          signal: controller.signal,
          cache: "no-store",
        });

        // `fetchAuthenticatedRaw` already throws on non-2xx, but keep a local guard
        // to avoid accidentally treating non-image payloads as images.
        const contentType = response.headers.get("content-type") || "";
        if (!contentType.toLowerCase().startsWith("image/")) {
          throw new Error(`Unexpected content-type for image: ${contentType || "(missing)"}`);
        }

        const blob = await response.blob();

        if (controller.signal.aborted || requestId !== requestIdRef.current) {
          return;
        }

        const blobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = blobUrl;
        setImageSrc(blobUrl);
        setIsLoading(false);
        thumbStats.fetchDone += 1;
        albumDebug("thumb.fetch done", {
          src,
          durationMs: Number((performance.now() - fetchStartedAt).toFixed(1)),
          limiterWaitMs,
          blobBytes: blob.size,
          fetchDone: thumbStats.fetchDone,
          fetchFailed: thumbStats.fetchFailed,
          limiter: mediaFetchLimiter.getStats(),
        });
      } catch (error) {
        if (!controller.signal.aborted && requestId === requestIdRef.current) {
          console.error("Error loading authenticated image:", error);
          setHasError(true);
          setIsLoading(false);
          thumbStats.fetchFailed += 1;
          albumDebug("thumb.fetch fail", {
            src,
            durationMs: Number((performance.now() - fetchStartedAt).toFixed(1)),
            error: error instanceof Error ? error.message : String(error),
            fetchFailed: thumbStats.fetchFailed,
            limiter: mediaFetchLimiter.getStats(),
          });
          onError?.();
        }
      } finally {
        window.clearTimeout(timeoutId);
        release?.();
      }
    };

    void fetchImage();

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
      // Clean up blob URL when component unmounts
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [shouldFetch, src, placeholder, onError]);

  const handleImageLoad = () => {
    setIsLoading(false);
    onLoad?.();
  };

  const handleImageError = () => {
    setHasError(true);
    setIsLoading(false);
    onError?.();
  };

  if (hasError) {
    // Show error placeholder
    return (
      <div
        data-section="authenticated-image-root"
        className={`${className} flex items-center justify-center bg-gray-700/50`}
      >
        <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </div>
    );
  }

  return (
    <>
      <img
        ref={imgRef}
        src={imageSrc}
        alt={alt}
        className={className}
        onLoad={handleImageLoad}
        onError={handleImageError}
        loading={loading}
      />
      {isLoading && imageSrc === placeholder && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-700/50">
          <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
      )}
    </>
  );
};

export default AuthenticatedImage;
